import { lazy, Suspense, useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/hooks/useAuth";
import { CartProvider } from "@/hooks/useCart";
import { useSessionTimeout } from "@/hooks/useSessionTimeout";
import { AdminRoute } from "@/components/AdminRoute";
import { PageTransition } from "@/components/PageTransition";

// Lazy load non-critical UI components that aren't visible on first paint
const CookieConsent = lazy(() => import("@/components/CookieConsent").then(m => ({ default: m.CookieConsent })));
const MiniCartDrawer = lazy(() => import("@/components/MiniCartDrawer").then(m => ({ default: m.MiniCartDrawer })));

// Lazy load all page components for code splitting
const Home = lazy(() => import("./pages/Home"));
const Shop = lazy(() => import("./pages/Shop"));
const Product = lazy(() => import("./pages/Product"));
const Cart = lazy(() => import("./pages/Cart"));
const Checkout = lazy(() => import("./pages/Checkout"));
const OrderConfirmation = lazy(() => import("./pages/OrderConfirmation"));
const OrderTracking = lazy(() => import("./pages/OrderTracking"));
const PaymentSuccess = lazy(() => import("./pages/PaymentSuccess"));
const PaymentCanceled = lazy(() => import("./pages/PaymentCanceled"));
const Account = lazy(() => import("./pages/Account"));
const Auth = lazy(() => import("./pages/Auth"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Contact = lazy(() => import("./pages/Contact"));
const SizeGuide = lazy(() => import("./pages/SizeGuide"));
const Shipping = lazy(() => import("./pages/Shipping"));
const FAQ = lazy(() => import("./pages/FAQ"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const AboutUs = lazy(() => import("./pages/AboutUs"));
const NotFound = lazy(() => import("./pages/NotFound"));


// Lazy load admin components
const AdminLayout = lazy(() => import("./pages/admin/AdminLayout").then(m => ({ default: m.AdminLayout })));
const Overview = lazy(() => import("./pages/admin/Overview"));
const Products = lazy(() => import("./pages/admin/Products"));
const ProductImages = lazy(() => import("./pages/admin/ProductImages"));
const Categories = lazy(() => import("./pages/admin/Categories"));
const Orders = lazy(() => import("./pages/admin/Orders"));
const Reviews = lazy(() => import("./pages/admin/Reviews"));
const DiscountCodes = lazy(() => import("./pages/admin/DiscountCodes"));
const Customers = lazy(() => import("./pages/admin/Customers"));
const UserManagement = lazy(() => import("./pages/admin/UserManagement"));
const Inventory = lazy(() => import("./pages/admin/Inventory"));
const AuditLogs = lazy(() => import("./pages/admin/AuditLogs"));
const SeedImages = lazy(() => import("./pages/admin/SeedImages"));
const Settings = lazy(() => import("./pages/admin/Settings"));
const Finance = lazy(() => import("./pages/admin/Finance"));
const EmailCampaigns = lazy(() => import("./pages/admin/EmailCampaigns"));
const EmailUnsubscribes = lazy(() => import("./pages/admin/EmailUnsubscribes"));
const Sales = lazy(() => import("./pages/admin/Sales"));
const SiteBranding = lazy(() => import("./pages/admin/SiteBranding"));

// Prefetch critical routes after initial load
const prefetchCriticalRoutes = () => {
  // Use requestIdleCallback for non-blocking prefetch, fallback to setTimeout
  const prefetch = () => {
    import("./pages/Shop");
    import("./pages/Product");
    import("./pages/Cart");
  };
  
  if ('requestIdleCallback' in window) {
    window.requestIdleCallback(prefetch, { timeout: 2000 });
  } else {
    setTimeout(prefetch, 1000);
  }
};

const queryClient = new QueryClient();

// Minimal loading fallback that doesn't cause layout shift
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
  </div>
);

function AppContent() {
  const { WarningDialog } = useSessionTimeout();

  // Prefetch critical routes after component mounts
  useEffect(() => {
    prefetchCriticalRoutes();
  }, []);

  return (
    <>
      <WarningDialog />
      <BrowserRouter>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<PageTransition><Home /></PageTransition>} />
            <Route path="/shop" element={<PageTransition><Shop /></PageTransition>} />
            <Route path="/shop/:categorySlug" element={<PageTransition><Shop /></PageTransition>} />
            <Route path="/product/:slug" element={<PageTransition><Product /></PageTransition>} />
            <Route path="/cart" element={<PageTransition><Cart /></PageTransition>} />
            <Route path="/checkout" element={<PageTransition><Checkout /></PageTransition>} />
            <Route path="/order-confirmation/:orderNumber" element={<PageTransition><OrderConfirmation /></PageTransition>} />
            <Route path="/payment-success" element={<PageTransition><PaymentSuccess /></PageTransition>} />
            <Route path="/payment-canceled" element={<PageTransition><PaymentCanceled /></PageTransition>} />
            <Route path="/track-order" element={<PageTransition><OrderTracking /></PageTransition>} />
            <Route path="/account" element={<PageTransition><Account /></PageTransition>} />
            <Route path="/auth" element={<PageTransition><Auth /></PageTransition>} />
            <Route path="/reset-password" element={<PageTransition><ResetPassword /></PageTransition>} />
            <Route path="/contact" element={<PageTransition><Contact /></PageTransition>} />
            <Route path="/size-guide" element={<PageTransition><SizeGuide /></PageTransition>} />
            <Route path="/shipping" element={<PageTransition><Shipping /></PageTransition>} />
            <Route path="/faq" element={<PageTransition><FAQ /></PageTransition>} />
            <Route path="/terms-of-service" element={<PageTransition><TermsOfService /></PageTransition>} />
            <Route path="/privacy-policy" element={<PageTransition><PrivacyPolicy /></PageTransition>} />
            <Route path="/about" element={<PageTransition><AboutUs /></PageTransition>} />
            
            {/* Admin Routes */}
            <Route
              path="/admin"
              element={
                <PageTransition>
                  <AdminRoute>
                    <AdminLayout>
                      <Overview />
                    </AdminLayout>
                  </AdminRoute>
                </PageTransition>
              }
            />
            <Route
              path="/admin/products"
              element={
                <PageTransition>
                  <AdminRoute>
                    <AdminLayout>
                      <Products />
                    </AdminLayout>
                  </AdminRoute>
                </PageTransition>
              }
            />
            <Route
              path="/admin/products/:productId/images"
              element={
                <PageTransition>
                  <AdminRoute>
                    <AdminLayout>
                      <ProductImages />
                    </AdminLayout>
                  </AdminRoute>
                </PageTransition>
              }
            />
            <Route
              path="/admin/categories"
              element={
                <PageTransition>
                  <AdminRoute>
                    <AdminLayout>
                      <Categories />
                    </AdminLayout>
                  </AdminRoute>
                </PageTransition>
              }
            />
            <Route
              path="/admin/orders"
              element={
                <PageTransition>
                  <AdminRoute>
                    <AdminLayout>
                      <Orders />
                    </AdminLayout>
                  </AdminRoute>
                </PageTransition>
              }
            />
            <Route
              path="/admin/reviews"
              element={
                <PageTransition>
                  <AdminRoute>
                    <AdminLayout>
                      <Reviews />
                    </AdminLayout>
                  </AdminRoute>
                </PageTransition>
              }
            />
            <Route
              path="/admin/customers"
              element={
                <PageTransition>
                  <AdminRoute>
                    <AdminLayout>
                      <Customers />
                    </AdminLayout>
                  </AdminRoute>
                </PageTransition>
              }
            />
            <Route
              path="/admin/users"
              element={
                <PageTransition>
                  <AdminRoute>
                    <AdminLayout>
                      <UserManagement />
                    </AdminLayout>
                  </AdminRoute>
                </PageTransition>
              }
            />
            <Route
              path="/admin/inventory"
              element={
                <PageTransition>
                  <AdminRoute>
                    <AdminLayout>
                      <Inventory />
                    </AdminLayout>
                  </AdminRoute>
                </PageTransition>
              }
            />
            <Route
              path="/admin/discount-codes"
              element={
                <PageTransition>
                  <AdminRoute>
                    <AdminLayout>
                      <DiscountCodes />
                    </AdminLayout>
                  </AdminRoute>
                </PageTransition>
              }
            />
            <Route
              path="/admin/finance"
              element={
                <PageTransition>
                  <AdminRoute>
                    <AdminLayout>
                      <Finance />
                    </AdminLayout>
                  </AdminRoute>
                </PageTransition>
              }
            />
            <Route
              path="/admin/email-campaigns"
              element={
                <PageTransition>
                  <AdminRoute>
                    <AdminLayout>
                      <EmailCampaigns />
                    </AdminLayout>
                  </AdminRoute>
                </PageTransition>
              }
            />
            <Route
              path="/admin/unsubscribes"
              element={
                <PageTransition>
                  <AdminRoute>
                    <AdminLayout>
                      <EmailUnsubscribes />
                    </AdminLayout>
                  </AdminRoute>
                </PageTransition>
              }
            />
            <Route
              path="/admin/sales"
              element={
                <PageTransition>
                  <AdminRoute>
                    <AdminLayout>
                      <Sales />
                    </AdminLayout>
                  </AdminRoute>
                </PageTransition>
              }
            />
            <Route
              path="/admin/site-branding"
              element={
                <PageTransition>
                  <AdminRoute>
                    <AdminLayout>
                      <SiteBranding />
                    </AdminLayout>
                  </AdminRoute>
                </PageTransition>
              }
            />
            <Route
              path="/admin/audit-logs"
              element={
                <PageTransition>
                  <AdminRoute>
                    <AdminLayout>
                      <AuditLogs />
                    </AdminLayout>
                  </AdminRoute>
                </PageTransition>
              }
            />
            <Route
              path="/admin/seed-images"
              element={
                <PageTransition>
                  <AdminRoute>
                    <AdminLayout>
                      <SeedImages />
                    </AdminLayout>
                  </AdminRoute>
                </PageTransition>
              }
            />
            <Route
              path="/admin/settings"
              element={
                <PageTransition>
                  <AdminRoute>
                    <AdminLayout>
                      <Settings />
                    </AdminLayout>
                  </AdminRoute>
                </PageTransition>
              }
            />
            
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<PageTransition><NotFound /></PageTransition>} />
          </Routes>
        </Suspense>
        <Suspense fallback={null}>
          <CookieConsent />
          <MiniCartDrawer />
        </Suspense>
      </BrowserRouter>
    </>
  );
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <CartProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <AppContent />
        </TooltipProvider>
      </CartProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
